import { BarChart3, Bell, MessageSquareWarning, SlidersHorizontal } from 'lucide-react';

import { defineArea } from '../../app/area';
import { RequirePermission } from '../../auth/guards';
import { ClaimDetailPage } from './pages/ClaimDetailPage';
import { ClaimFormPage } from './pages/ClaimFormPage';
import { ClaimStatisticsPage } from './pages/ClaimStatisticsPage';
import { ClaimStatusesPage } from './pages/ClaimStatusesPage';
import { ClaimsListPage } from './pages/ClaimsListPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { PortalClaimsPage } from './pages/PortalClaimsPage';

/**
 * OWNER: the `claims` module agent (M7 — docs/MODULES.md; also owns the
 * notification centre and the transactional mail templates).
 * Owns: apps/web/src/areas/claims/**  ·  apps/api/src/modules/claims/**
 *       packages/shared/src/contracts/claims.ts
 *       packages/shared/src/i18n/{sv,en,zh}/claims.json  ·  e2e/claims.md
 *
 * Route order matters inside an area: `orders/claims/new` and
 * `orders/claims/statistics` are declared BEFORE `orders/claims/:id`, otherwise
 * react-router would happily treat "new" as a claim id.
 *
 * The notification centre is registered in BOTH chromes. The BELL itself is
 * `components/NotificationBell.tsx`, a drop-in the shell mounts in `AppTopBar`
 * (a shell-owned file this agent must not edit — see the component's header).
 */
export default defineArea({
  id: 'claims',
  nav: [
    {
      section: 'orders',
      to: '/orders/claims',
      labelKey: 'common:nav.claims.list',
      icon: MessageSquareWarning,
      permissions: ['claims.read'],
      order: 50,
      end: true,
    },
    {
      section: 'orders',
      to: '/orders/claims/statistics',
      labelKey: 'common:nav.claims.statistics',
      icon: BarChart3,
      permissions: ['claims.statistics'],
      order: 55,
    },
    {
      section: 'system',
      to: '/orders/claims/statuses',
      labelKey: 'claims:statusCatalog.title',
      icon: SlidersHorizontal,
      permissions: ['claims.manage'],
      order: 30,
    },
    {
      // `system`, not `overview`: the shell's nav contract test pins the
      // overview group to the dashboard alone, and the notification centre is
      // an account-level surface anyway (it sits next to Settings).
      section: 'system',
      to: '/notifications',
      labelKey: 'common:nav.notifications',
      icon: Bell,
      order: 10,
    },
  ],
  routes: [
    {
      path: 'orders/claims',
      element: (
        <RequirePermission permissions={['claims.read']}>
          <ClaimsListPage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/claims/new',
      element: (
        <RequirePermission permissions={['claims.create']}>
          <ClaimFormPage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/claims/statistics',
      element: (
        <RequirePermission permissions={['claims.statistics']}>
          <ClaimStatisticsPage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/claims/statuses',
      element: (
        <RequirePermission permissions={['claims.manage']}>
          <ClaimStatusesPage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/claims/:id',
      element: (
        <RequirePermission permissions={['claims.read']}>
          <ClaimDetailPage />
        </RequirePermission>
      ),
    },
    { path: 'notifications', element: <NotificationsPage /> },
  ],
  portalNav: [
    {
      to: '/portal/claims',
      labelKey: 'shell:portal.claims',
      icon: MessageSquareWarning,
      permissions: ['claims.read', 'claims.create'],
      order: 40,
      end: true,
    },
    // NOTE: no portal nav entry for `/portal/notifications` — the portal bar is
    // four large tap targets by design and the shell's nav contract test pins
    // its exact contents. The route exists and the bell links to it; mounting
    // `<NotificationBell portal />` in the portal chrome is the intended entry
    // point (see components/NotificationBell.tsx).
  ],
  portalRoutes: [
    {
      path: 'claims',
      element: (
        <RequirePermission permissions={['claims.read']}>
          <PortalClaimsPage />
        </RequirePermission>
      ),
    },
    {
      path: 'claims/new',
      element: (
        <RequirePermission permissions={['claims.create']}>
          <ClaimFormPage portal />
        </RequirePermission>
      ),
    },
    {
      path: 'claims/:id',
      element: (
        <RequirePermission permissions={['claims.read']}>
          <ClaimDetailPage portal />
        </RequirePermission>
      ),
    },
    { path: 'notifications', element: <NotificationsPage portal /> },
  ],
});
